import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '../../api/client';
import { usePassOrder, usePassOrderInvoice } from '../../api/hooks';
import { passOrderSchema } from '../../api/types';
import { haptics, openInvoice } from '../../telegram';
import { track } from '../../analytics';

/**
 * Stars-оплата заказа пасса (колоды или Pass+): создать заказ provider
 * 'stars' → инвойс-ссылка → openInvoice; по 'paid' инвалидируются ['pass'].
 * Если у игрока уже висит desk-pending — просто открываем его лист (onDone).
 *
 * Колбэк openInvoice в некоторых клиентах Telegram теряется (панель оплаты
 * «зависает», экран не обновляется) — поэтому параллельно поллим активный
 * заказ: как только он перестал быть pending (вебхук отметил оплату или
 * заказ отменён/истёк), обновляем ['pass'] сами.
 */
export function useStarsOrder(onDone: () => void) {
  const order = usePassOrder();
  const invoice = usePassOrderInvoice();
  const qc = useQueryClient();

  const settle = () => {
    haptics.success();
    void qc.invalidateQueries({ queryKey: ['pass'] });
  };

  /** Страховка: ждём, пока заказ исчезнет из pending (до 3 минут). */
  const watchOrder = (code: string) => {
    const t0 = Date.now();
    const tick = async () => {
      if (Date.now() - t0 > 180_000) return;
      try {
        const { order: active } = await api.get(
          '/pass/orders/active',
          z.object({ order: passOrderSchema.nullable() }),
        );
        if (active?.code === code && active.status === 'pending') {
          window.setTimeout(() => void tick(), 2500);
          return;
        }
        // Заказ разрешился (оплачен/отменён/истёк) — показываем свежее состояние.
        void qc.invalidateQueries({ queryKey: ['pass'] });
      } catch {
        window.setTimeout(() => void tick(), 5000);
      }
    };
    window.setTimeout(() => void tick(), 2500);
  };

  const buy = async (
    params: { kind: 'plus' } | { kind: 'decks'; deckRarity: number; qty: number },
  ) => {
    haptics.tap();
    track('pass_order_create', { ...params, provider: 'stars' });
    const created = await order.mutateAsync({ ...params, provider: 'stars' });
    // Вернулся существующий desk-pending — покажем его лист (там отмена).
    if (created.provider !== 'stars') return onDone();
    const { link } = await invoice.mutateAsync(created.code);
    watchOrder(created.code);
    openInvoice(link, (status) => {
      if (status === 'paid') settle();
      onDone();
    });
  };
  return { buy, pending: order.isPending || invoice.isPending };
}

export interface PayMethod {
  key: string;
  /** «Оплатить 1 500 ⭐ в Telegram», «Оплата у администратора — 3 990 ₽» */
  label: string;
  primary?: boolean;
  disabled?: boolean;
  onPick: () => void;
}

/**
 * Единый выбор способа оплаты (решение владельца 02.10): стопка кнопок —
 * первым идёт основной способ, дальше альтернативы. Новые способы оплаты
 * добавляются элементом массива `methods`.
 */
export function PayMethods({ methods }: { methods: PayMethod[] }) {
  return (
    <div className="bp-paymethods">
      {methods.map((m, i) => (
        <button
          key={m.key}
          className={m.primary ?? i === 0 ? 'btn' : 'btn-secondary bp-btn-secondary'}
          disabled={m.disabled}
          onClick={m.onPick}
        >
          {m.label}
        </button>
      ))}
    </div>
  );
}
